import { Box, Stack, Typography } from "@mui/material";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { AverageTransactionValueDropdown } from "Components/Shared/SelectorsForm/AverageTransactionValueDropdown";
import { BusinessActivityDropdown } from "Components/Shared/SelectorsForm/BusinessActivityDropdown";
import { ExpectedMonthlyRevenueDropdown } from "Components/Shared/SelectorsForm/ExpectedMonthlyRevenueDropdown";
import { IncomeSourceCheckboxGroup } from "Components/Shared/SelectorsForm/IncomeSourceCheckboxGroup";
import type { UserVerificationUpdateFormData } from "Components/Users/schemas";
import { BusinessActivity, SourceOfIncome } from "Infrastructure/Api/Api";
import type { Control, UseFormWatch } from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Verification.Form.Fields;

type VerificationFormFieldsProps = {
  control: Control<UserVerificationUpdateFormData>;
  watch: UseFormWatch<UserVerificationUpdateFormData>;
  legacySourceOfIncome?: string | null;
};

export const VerificationFormFields = ({
  control,
  watch,
  legacySourceOfIncome,
}: VerificationFormFieldsProps) => {
  const { t } = useResource();
  const businessActivity = watch("businessActivity");
  const sourcesOfIncome = watch("sourcesOfIncome");

  return (
    <Box component="fieldset" sx={{ all: "unset", display: "grid" }}>
      <CountryDropdown
        control={control}
        name="taxResidency"
        label={t(COMPONENT_RESOURCES.TaxResidencyCountryID)}
      />
      <BusinessActivityDropdown
        control={control}
        name="businessActivity"
        label={t(COMPONENT_RESOURCES.BusinessActivity)}
        includeLegacy
      />
      {businessActivity === BusinessActivity.OtherAndPublicServices && (
        <FormInput
          control={control}
          name="otherBusinessActivityDetails"
          label={t(COMPONENT_RESOURCES.OtherBusinessActivityDetails)}
        />
      )}
      <Typography variant="body2" color="text.secondary">
        <strong>{t(COMPONENT_RESOURCES.LegacySourceOfIncome)}:</strong>{" "}
        {legacySourceOfIncome || t(Resources.Common.Unknown)}
      </Typography>
      <IncomeSourceCheckboxGroup
        control={control}
        name="sourcesOfIncome"
        label={t(COMPONENT_RESOURCES.SourceOfIncome)}
      />
      {sourcesOfIncome?.includes(SourceOfIncome.OtherBusinessIncome) && (
        <FormInput
          control={control}
          name="otherSourceOfIncomeDetails"
          label={t(COMPONENT_RESOURCES.OtherSourceOfIncomeDetails)}
        />
      )}
      <ExpectedMonthlyRevenueDropdown
        control={control}
        name="expectedMonthlyRevenue"
        label={t(COMPONENT_RESOURCES.ExpectedMonthlyRevenue)}
      />
      <AverageTransactionValueDropdown
        control={control}
        name="averageTransactionValue"
        label={t(COMPONENT_RESOURCES.AverageTransactionValue)}
      />
      <Stack gap={1} mb="1rem">
        <CheckboxInput
          control={control}
          name="isPoliticallyExposedPerson"
          label={t(COMPONENT_RESOURCES.IsPoliticallyExposedPerson)}
        />
        <CheckboxInput
          label={t(COMPONENT_RESOURCES.HasNonEUResidence)}
          control={control}
          name="hasNonEUResidence"
        />
        <CheckboxInput
          label={t(COMPONENT_RESOURCES.HasNonEUNationality)}
          control={control}
          name="hasNonEUNationality"
        />
      </Stack>
    </Box>
  );
};
